import { BATTLE_REEL_TIMINGS, capitalizeTerm, formatAmountWithUnit } from "@loot-os/api-contract";
import { isLootApiErrorLike, useClientSeed } from "@loot-os/react";
import { Button } from "@loot-os/ui/components/button";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { BattleCompletedPanel } from "../components/battles/BattleCompletedPanel.js";
import { BattleFairnessPanel } from "../components/battles/BattleFairnessPanel.js";
import { BattleLiveStage } from "../components/battles/BattleLiveStage.js";
import { BattleModeBadge } from "../components/battles/BattleModeBadge.js";
import { BattleResults } from "../components/battles/BattleResults.js";
import { BattleRoundsStrip, type RoundBox } from "../components/battles/BattleRoundsStrip.js";
import { BattleSeatTiles } from "../components/battles/BattleSeatTiles.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { useBattleMachine } from "../lib/battleMachine.js";
import {
  useAddBot,
  useClaimBattle,
  useJoinBattle,
  useSellBackBattle,
  useStartBattle,
} from "../lib/battles.js";
import {
  canAnyoneStart,
  countdownMsOf,
  liveTimelineRoundIndex,
  resyncTargetRound,
  visualRevealAtMs,
} from "../lib/battleTimeline.js";
import { useCurrency } from "../lib/currency.js";
import { battleQueryOptions, qk } from "../lib/queries.js";
import { queryClient } from "../lib/queryClient.js";
import { pendingRevealFromError, useRevealClaim } from "../lib/revealClaim.js";
import { serverNow } from "../lib/serverClock.js";
import { useShopper } from "../lib/shopper.js";
import { useTerminology } from "../lib/terminology.js";
import { useBattleChannel } from "../lib/useBattleChannel.js";

const NO_LANES: ReadonlySet<number> = new Set();

/**
 * One battle: lobby → countdown → live rounds → results. Server data is canonical (polled or
 * pushed); the stage machine decides what shows when, the timeline math when a round may render.
 */
export function BattlePage() {
  const { battleId } = useParams({ from: "/battles/$battleId" });
  const { replay: replayParam } = useSearch({ from: "/battles/$battleId" });
  const { user, sessionToken, authPending, sessionPending } = useShopper();
  const currency = useCurrency();
  const { battle: term } = useTerminology();
  const navigate = useNavigate();
  const live = useBattleChannel(battleId);
  const [clientSeed] = useClientSeed({});

  // Wait for shopper auth to settle before the first fetch: a completed battle stops polling, so a
  // spectator response fetched during the auth → session handoff would cache `me: null` and never
  // refetch, hiding the claim from a seated winner. A signed-out visitor settles immediately.
  const authSettled = !authPending && !sessionPending;
  const query = useQuery({
    ...battleQueryOptions(battleId, { poll: !live }),
    enabled: !!battleId && authSettled,
  });
  const battle = query.data?.battle ?? null;
  const loadError = query.error
    ? query.error instanceof Error
      ? query.error.message
      : `failed to load the ${term.singular}`
    : null;

  // The viewer's Loot end-user id comes from the `me` block, the one place the server names it.
  const viewerEndUserId = battle?.me
    ? (battle.seats[battle.me.seatIndex]?.endUser?.id ?? null)
    : null;
  const isCreator = battle?.me?.seatIndex === 0;

  const snapshot = useMemo(
    () =>
      battle
        ? {
            status: battle.status,
            currentRevealedRound: battle.currentRevealedRound,
            roundCount: battle.roundCount,
            startedAtMs: battle.startedAt ? Date.parse(battle.startedAt) : null,
            startCountdownMs: countdownMsOf(battle),
            revealIntervalMs: battle.revealIntervalMs,
          }
        : null,
    [battle],
  );
  const { machine, state } = useBattleMachine(snapshot, serverNow);

  // Show + hold after the reels land, then move on.
  useEffect(() => {
    if (state.stage !== "live" || state.roundPhase !== "showing" || !battle) return;
    const hold = battle.fastSpin ? BATTLE_REEL_TIMINGS.quickHoldMs : BATTLE_REEL_TIMINGS.holdMs;
    const timer = setTimeout(() => machine.holdFinished(), hold);
    return () => clearTimeout(timer);
  }, [state.stage, state.roundPhase, battle, machine]);

  // A live round may render only once its boundary on the server's clock has passed.
  const roundGateMs =
    battle && state.stage === "live" && !state.replaying
      ? visualRevealAtMs(battle, state.round)
      : null;
  const gateOpen = useGate(roundGateMs);

  // The seats whose reel has landed this round, so the results grid lights their card up as it
  // lands rather than waiting for the last lane.
  const [settled, setSettled] = useState<{ round: number; lanes: ReadonlySet<number> }>({
    round: -1,
    lanes: NO_LANES,
  });
  const settledLanes = settled.round === state.round ? settled.lanes : NO_LANES;
  const onLaneSettled = useCallback(
    (seatIndex: number) => {
      setSettled((prev) => {
        const lanes = new Set(prev.round === state.round ? prev.lanes : []);
        lanes.add(seatIndex);
        return { round: state.round, lanes };
      });
    },
    [state.round],
  );

  // Replay auto-start from ?replay=1, once the battle is completed.
  const replayedRef = useRef(false);
  useEffect(() => {
    if (replayParam && battle?.status === "completed" && !replayedRef.current) {
      replayedRef.current = true;
      machine.replay();
      void navigate({ to: "/battles/$battleId", params: { battleId }, search: {}, replace: true });
    }
  }, [replayParam, battle?.status, machine, navigate, battleId]);

  // After a real tab-out, catch up: refetch and snap to where the timeline is.
  const hiddenRef = useRef(false);
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenRef.current = true;
        return;
      }
      if (!hiddenRef.current) return;
      hiddenRef.current = false;
      if (!battle || state.stage !== "live" || state.replaying) return;
      const timelineRound = liveTimelineRoundIndex(battle, serverNow());
      if (timelineRound === null || timelineRound <= state.round) return;
      const key = qk.battle(battleId);
      void queryClient.refetchQueries({ queryKey: key }).then(() => {
        const fresh = queryClient.getQueryData<typeof query.data>(key)?.battle;
        if (!fresh) return;
        machine.snapTo(
          resyncTargetRound({
            currentRevealedRound: fresh.currentRevealedRound,
            timelineRound,
            roundCount: fresh.roundCount,
          }),
        );
      });
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [battle, state.stage, state.round, state.replaying, machine, battleId]);

  const join = useJoinBattle(battleId);
  const addBot = useAddBot(battleId);
  const start = useStartBattle(battleId);
  const claim = useClaimBattle(battleId);
  const sellBack = useSellBackBattle(battleId);
  const [actionError, setActionError] = useState<string | null>(null);
  const pending = join.isPending || addBot.isPending || start.isPending;
  const describe = useCallback(
    (err: unknown, fallback: string) => {
      if (isLootApiErrorLike(err)) {
        if (err.code === "insufficient_balance")
          return `Not enough ${currency.namePlural} to enter.`;
        if (err.code === "reveal_pending") return "Claim your pending reveal first.";
        if (err.code === "battle_seat_taken") return "That seat was just taken.";
        if (err.code === "battle_not_joinable") return `This ${term.singular} has already started.`;
        if (err.code === "battles_disabled")
          return `${capitalizeTerm(term.plural)} are switched off for this store.`;
        return err.message || fallback;
      }
      return err instanceof Error ? err.message : fallback;
    },
    [currency.namePlural, term],
  );

  const { promptClaim } = useRevealClaim();
  // A blocked action means an unclaimed reveal: open the claim modal instead of a dead-end message.
  const handleActionError = useCallback(
    (err: unknown, fallback: string) => {
      const hint = pendingRevealFromError(err);
      if (hint) {
        setActionError(null);
        promptClaim(hint);
        return;
      }
      setActionError(describe(err, fallback));
    },
    [describe, promptClaim],
  );

  const boxMap = useMemo(() => {
    const map = new Map<string, RoundBox>();
    for (const version of battle?.boxVersions ?? []) {
      map.set(version.boxId, { name: version.name, imageUrl: version.imageUrl });
    }
    return map;
  }, [battle?.boxVersions]);

  const onRoundAnimationFinished = useCallback(() => machine.roundAnimationFinished(), [machine]);
  const onCountdownFinished = useCallback(() => machine.countdownFinished(), [machine]);

  if (!battleId) return null;
  if (loadError) return <ErrorBanner>{loadError}</ErrorBanner>;
  if (!battle) return <Skeleton className="h-64 rounded-xl" />;

  // The last round every seat shows in the results, and the round whose landed seats show too.
  const shownThrough =
    state.stage === "completed"
      ? battle.roundCount - 1
      : state.stage === "live"
        ? state.roundPhase === "showing"
          ? state.round
          : state.round - 1
        : -1;
  const currentRound = state.stage === "live" ? state.round : -1;
  const displayRound =
    state.stage === "completed"
      ? battle.roundCount
      : state.stage === "live"
        ? state.round + 1
        : state.stage === "countdown"
          ? 1
          : 0;
  const countdownEndsAt = state.replaying
    ? (state.replayStartedAtMs ?? serverNow()) + countdownMsOf(battle)
    : battle.startedAt
      ? Date.parse(battle.startedAt) + countdownMsOf(battle)
      : null;

  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            {battle.playerCount}-player battle
          </h1>
          <BattleModeBadge mode={battle.mode} />
          {battle.fastSpin ? (
            <span className="text-xs text-muted-foreground">Fast spin</span>
          ) : null}
          {!battle.isPublic ? <span className="text-xs text-muted-foreground">Private</span> : null}
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            void navigator.clipboard?.writeText(window.location.href);
          }}
        >
          Copy link
        </Button>
      </div>

      {/* The info bar: rounds and entry on the left, the boxes in round order across the middle. */}
      <div className="flex flex-wrap items-center gap-6 rounded-md border border-border bg-card px-4 py-3">
        <div className="flex items-center gap-6">
          <div className="grid leading-tight">
            <span className="text-base font-bold tabular-nums">
              {displayRound}
              <span className="text-xs font-semibold text-muted-foreground">
                /{battle.roundCount}
              </span>
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Rounds
            </span>
          </div>
          <div className="grid leading-tight">
            <span className="text-base font-bold">
              {formatAmountWithUnit(battle.priceCents, currency)}
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Entry
            </span>
          </div>
        </div>
        <div className="flex min-w-0 flex-1 justify-center overflow-x-auto">
          <BattleRoundsStrip
            rounds={battle.rounds}
            boxes={boxMap}
            currentRound={
              state.stage === "live" ? state.round : state.stage === "countdown" ? 0 : -1
            }
            max={12}
          />
        </div>
      </div>

      {actionError && <ErrorBanner>{actionError}</ErrorBanner>}

      {state.stage === "waiting" ? (
        <BattleSeatTiles
          battle={battle}
          viewerEndUserId={viewerEndUserId}
          signedIn={!!user && !!sessionToken}
          isCreator={isCreator}
          canStartAsStranger={canAnyoneStart(battle, serverNow())}
          pending={pending}
          onJoin={(seatIndex) => {
            setActionError(null);
            join.mutate(
              { seatIndex, clientSeed },
              { onError: (err) => handleActionError(err, "could not join") },
            );
          }}
          onAddBot={(seatIndex) => {
            setActionError(null);
            addBot.mutate(
              { seatIndex },
              { onError: (err) => handleActionError(err, "could not add a bot") },
            );
          }}
          onStart={() => {
            setActionError(null);
            start.mutate(undefined, {
              onError: (err) => handleActionError(err, "could not start"),
            });
          }}
        />
      ) : null}

      {state.stage === "countdown" || state.stage === "live" ? (
        <BattleLiveStage
          battle={battle}
          state={state}
          viewerEndUserId={viewerEndUserId}
          countdownEndsAtMs={countdownEndsAt}
          gateOpen={gateOpen || state.replaying}
          onCountdownFinished={onCountdownFinished}
          onLaneSettled={onLaneSettled}
          onRoundAnimationFinished={onRoundAnimationFinished}
        />
      ) : null}

      {state.stage === "completed" ? (
        <BattleCompletedPanel
          battle={battle}
          viewerEndUserId={viewerEndUserId}
          claiming={claim.isPending}
          claimError={claim.error ? describe(claim.error, "could not claim") : null}
          onClaim={() => claim.mutate()}
          sellingBack={sellBack.isPending}
          sellBackError={sellBack.error ? describe(sellBack.error, "could not sell back") : null}
          onSellBack={() => sellBack.mutate()}
          onReplay={() => machine.replay()}
        />
      ) : null}

      {state.stage !== "waiting" ? (
        <BattleResults
          battle={battle}
          throughRound={shownThrough}
          currentRound={currentRound}
          settledLanes={settledLanes}
          viewerEndUserId={viewerEndUserId}
        />
      ) : null}

      <BattleFairnessPanel battle={battle} />
    </div>
  );
}

/** True once the server clock has passed `atMs` (immediately when null); re-renders at the instant. */
function useGate(atMs: number | null): boolean {
  const [, tick] = useState(0);
  const open = atMs === null || serverNow() >= atMs;
  useEffect(() => {
    if (open || atMs === null) return;
    const timer = setTimeout(() => tick((n) => n + 1), Math.max(0, atMs - serverNow()) + 5);
    return () => clearTimeout(timer);
  }, [atMs, open]);
  return open;
}
